/**
 * JsonDiffViewer — recursive field-level diff viewer for two JSON objects.
 *
 * Shows added (green), removed (red), and changed (amber) fields with
 * nested indentation. Arrays are diffed element-by-element.
 */

import { useState, useMemo, useEffect, type ReactNode } from 'react';
import './JsonDiffViewer.css';

type DiffSegment = { value: string; added?: boolean; removed?: boolean };
type DiffWordsFn = (a: string, b: string) => DiffSegment[];

let _diffWordsPromise: Promise<DiffWordsFn> | null = null;
function loadDiffWords(): Promise<DiffWordsFn> {
  if (!_diffWordsPromise) {
    _diffWordsPromise = import('diff').then(m => m.diffWordsWithSpace as unknown as DiffWordsFn);
  }
  return _diffWordsPromise;
}

interface Props {
  before: unknown;
  after: unknown;
  /** Fields to exclude from comparison (e.g. Cosmos metadata). */
  ignoreFields?: Set<string>;
  /** Optional node rendered on the right side of the summary header
   *  (e.g. the question id), aligned with the diff view controls. */
  title?: ReactNode;
}

type DiffKind = 'added' | 'removed' | 'changed' | 'unchanged' | 'nested';

interface DiffEntry {
  key: string;
  kind: DiffKind;
  before?: unknown;
  after?: unknown;
  children?: DiffEntry[];
}

const DEFAULT_IGNORE = new Set([
  '_rid', '_self', '_etag', '_attachments', '_ts',
  'updatedAt', 'updatedBy', 'createdAt', 'createdBy',
  'version',
]);

function isObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  if (typeof a === 'object') {
    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const keys = new Set([...Object.keys(aObj), ...Object.keys(bObj)]);
    for (const k of keys) {
      if (!deepEqual(aObj[k], bObj[k])) return false;
    }
    return true;
  }
  return false;
}

function computeDiff(
  before: unknown,
  after: unknown,
  ignore: Set<string>,
): DiffEntry[] {
  const entries: DiffEntry[] = [];

  if (isObject(before) && isObject(after)) {
    const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const key of allKeys) {
      if (ignore.has(key)) continue;

      const inBefore = key in before;
      const inAfter = key in after;

      if (!inBefore) {
        entries.push({ key, kind: 'added', after: after[key] });
      } else if (!inAfter) {
        entries.push({ key, kind: 'removed', before: before[key] });
      } else if (deepEqual(before[key], after[key])) {
        entries.push({ key, kind: 'unchanged', before: before[key], after: after[key] });
      } else if (isObject(before[key]) && isObject(after[key])) {
        const children = computeDiff(before[key], after[key], ignore);
        const hasChanges = children.some(c => c.kind !== 'unchanged');
        entries.push({
          key,
          kind: hasChanges ? 'nested' : 'unchanged',
          before: before[key],
          after: after[key],
          children: hasChanges ? children : undefined,
        });
      } else if (Array.isArray(before[key]) && Array.isArray(after[key])) {
        const children = diffArray(before[key], after[key], ignore);
        const hasChanges = children.some(c => c.kind !== 'unchanged');
        entries.push({
          key,
          kind: hasChanges ? 'nested' : 'unchanged',
          before: before[key],
          after: after[key],
          children: hasChanges ? children : undefined,
        });
      } else {
        entries.push({ key, kind: 'changed', before: before[key], after: after[key] });
      }
    }
  }

  // Sort: changed/added/removed first, then unchanged
  const order: Record<DiffKind, number> = {
    changed: 0, added: 1, removed: 2, nested: 3, unchanged: 4,
  };
  entries.sort((a, b) => order[a.kind] - order[b.kind]);

  return entries;
}

/** Diff two arrays element-by-element. For object arrays, try to match by id/name. */
function diffArray(
  before: unknown[],
  after: unknown[],
  ignore: Set<string>,
): DiffEntry[] {
  // If items have `id` or `name`, match by that key
  const matchKey = getArrayMatchKey(before, after);
  if (matchKey) {
    return diffArrayByKey(before as Record<string, unknown>[], after as Record<string, unknown>[], matchKey, ignore);
  }

  // Positional diff
  const entries: DiffEntry[] = [];
  const maxLen = Math.max(before.length, after.length);
  for (let i = 0; i < maxLen; i++) {
    const key = `[${i}]`;
    if (i >= before.length) {
      entries.push({ key, kind: 'added', after: after[i] });
    } else if (i >= after.length) {
      entries.push({ key, kind: 'removed', before: before[i] });
    } else if (deepEqual(before[i], after[i])) {
      entries.push({ key, kind: 'unchanged', before: before[i], after: after[i] });
    } else if (isObject(before[i]) && isObject(after[i])) {
      const children = computeDiff(before[i], after[i], ignore);
      const hasChanges = children.some(c => c.kind !== 'unchanged');
      entries.push({
        key,
        kind: hasChanges ? 'nested' : 'unchanged',
        before: before[i],
        after: after[i],
        children: hasChanges ? children : undefined,
      });
    } else {
      entries.push({ key, kind: 'changed', before: before[i], after: after[i] });
    }
  }
  return entries;
}

function getArrayMatchKey(before: unknown[], after: unknown[]): string | null {
  const all = [...before, ...after];
  if (all.length === 0 || !all.every(isObject)) return null;
  const objs = all as Record<string, unknown>[];
  if (objs.every(o => typeof o.id === 'string')) return 'id';
  if (objs.every(o => typeof o.name === 'string')) return 'name';
  return null;
}

function diffArrayByKey(
  before: Record<string, unknown>[],
  after: Record<string, unknown>[],
  matchKey: string,
  ignore: Set<string>,
): DiffEntry[] {
  const entries: DiffEntry[] = [];
  const beforeMap = new Map(before.map(o => [o[matchKey] as string, o]));
  const afterMap = new Map(after.map(o => [o[matchKey] as string, o]));

  for (const [k, afterObj] of afterMap) {
    const beforeObj = beforeMap.get(k);
    const label = k;
    if (!beforeObj) {
      entries.push({ key: label, kind: 'added', after: afterObj });
    } else if (deepEqual(beforeObj, afterObj)) {
      entries.push({ key: label, kind: 'unchanged', before: beforeObj, after: afterObj });
    } else {
      const children = computeDiff(beforeObj, afterObj, ignore);
      const hasChanges = children.some(c => c.kind !== 'unchanged');
      entries.push({
        key: label,
        kind: hasChanges ? 'nested' : 'unchanged',
        before: beforeObj,
        after: afterObj,
        children: hasChanges ? children : undefined,
      });
    }
  }

  for (const [k, beforeObj] of beforeMap) {
    if (!afterMap.has(k)) {
      entries.push({ key: k, kind: 'removed', before: beforeObj });
    }
  }

  return entries;
}

function formatValue(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return 'undefined';
  if (typeof v === 'string') return `"${v}"`;
  if (typeof v === 'object') return JSON.stringify(v, null, 2);
  return String(v);
}

function formatCompactValue(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return 'undefined';
  if (typeof v === 'string') return `"${v}"`;
  if (Array.isArray(v)) return `[${v.length} items]`;
  if (typeof v === 'object') {
    const keys = Object.keys(v);
    return `{${keys.length} fields}`;
  }
  return String(v);
}

// ── Rendering ──────────────────────────────────────────────────────────

function DiffEntryRow({ entry, depth }: { entry: DiffEntry; depth: number }) {
  const [expanded, setExpanded] = useState(entry.kind !== 'unchanged');
  // Long strings also get an expandable view (side-by-side before/after) so
  // the full text is readable even when the compact inline preview truncates.
  const LONG_STRING_THRESHOLD = 60;
  const isLongString = (v: unknown): boolean => typeof v === 'string' && v.length > LONG_STRING_THRESHOLD;
  const isExpandable =
    entry.children != null
    || isObject(entry.after) || isObject(entry.before)
    || Array.isArray(entry.after) || Array.isArray(entry.before)
    || (entry.kind === 'changed' && (isLongString(entry.before) || isLongString(entry.after)))
    || (entry.kind === 'added' && isLongString(entry.after))
    || (entry.kind === 'removed' && isLongString(entry.before));
  const hasInlineValue = entry.kind === 'changed' || entry.kind === 'added' || entry.kind === 'removed';

  if (entry.kind === 'unchanged' && depth > 0) {
    return null; // Hide unchanged fields in nested views
  }

  return (
    <div className="jdv-entry">
      <div
        className={`jdv-row jdv-row--${entry.kind}`}
        style={{ paddingLeft: depth * 20 + 8 }}
        onClick={isExpandable ? () => setExpanded(!expanded) : undefined}
      >
        {isExpandable && (
          <span className="jdv-expand">{expanded ? '▼' : '▶'}</span>
        )}
        <span className="jdv-key">{entry.key}</span>
        <span className={`jdv-badge jdv-badge--${entry.kind}`}>
          {entry.kind === 'added' && 'ADDED'}
          {entry.kind === 'removed' && 'REMOVED'}
          {entry.kind === 'changed' && 'CHANGED'}
          {entry.kind === 'nested' && 'MODIFIED'}
        </span>
        {hasInlineValue && (
          <span className="jdv-inline-value">
            {entry.kind === 'changed' && (
              <>
                <span className="jdv-val-removed">{formatCompactValue(entry.before)}</span>
                <span className="jdv-arrow">→</span>
                <span className="jdv-val-added">{formatCompactValue(entry.after)}</span>
              </>
            )}
            {entry.kind === 'added' && (
              <span className="jdv-val-added">{formatCompactValue(entry.after)}</span>
            )}
            {entry.kind === 'removed' && (
              <span className="jdv-val-removed">{formatCompactValue(entry.before)}</span>
            )}
          </span>
        )}
      </div>

      {expanded && entry.children && (
        <div className="jdv-children">
          {entry.children.filter(c => c.kind !== 'unchanged').map(child => (
            <DiffEntryRow key={child.key} entry={child} depth={depth + 1} />
          ))}
        </div>
      )}

      {expanded && !entry.children && isExpandable && (
        <div className="jdv-expanded-value" style={{ marginLeft: depth * 20 + 28 }}>
          {entry.kind === 'changed' && isLongString(entry.before) && isLongString(entry.after) ? (
            <TextDiffViewer before={entry.before as string} after={entry.after as string} />
          ) : entry.kind === 'changed' && (
            <div className="jdv-side-by-side">
              <div className="jdv-side jdv-side--before">
                <div className="jdv-side-label">Before</div>
                <pre className="jdv-pre">{formatValue(entry.before)}</pre>
              </div>
              <div className="jdv-side jdv-side--after">
                <div className="jdv-side-label">After</div>
                <pre className="jdv-pre">{formatValue(entry.after)}</pre>
              </div>
            </div>
          )}
          {entry.kind === 'added' && (
            <pre className="jdv-pre jdv-pre--added">{formatValue(entry.after)}</pre>
          )}
          {entry.kind === 'removed' && (
            <pre className="jdv-pre jdv-pre--removed">{formatValue(entry.before)}</pre>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Word-level diff of two blocks of text, with an inline/side-by-side toggle.
 *
 * Exported because a whole *view* can be a diff, not just an expanded field
 * inside JsonDiffViewer — the prompt console shows one of these per tab
 * (system prompt, user prompt, schema) when browsing version history.
 */
export function TextDiffViewer({ before, after }: { before: string; after: string }) {
  const [mode, setMode] = useState<'inline' | 'side-by-side'>('inline');
  const [segments, setSegments] = useState<DiffSegment[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (mode !== 'inline' || segments) return;
    let cancelled = false;
    loadDiffWords()
      .then(diffWords => { if (!cancelled) setSegments(diffWords(before, after)); })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, [mode, segments, before, after]);

  const showSideBySide = mode === 'side-by-side' || loadFailed;

  return (
    <div className="jdv-long-string">
      <div className="jdv-long-string-toolbar">
        <button
          type="button"
          className={`jdv-long-string-tab${!showSideBySide ? ' jdv-long-string-tab--active' : ''}`}
          onClick={() => setMode('inline')}
        >
          Inline diff
        </button>
        <button
          type="button"
          className={`jdv-long-string-tab${showSideBySide ? ' jdv-long-string-tab--active' : ''}`}
          onClick={() => setMode('side-by-side')}
        >
          Side-by-side
        </button>
      </div>

      {!showSideBySide && (
        segments
          ? <pre className="jdv-pre jdv-inline-diff">
              {segments.map((s, i) => (
                <span
                  key={i}
                  className={s.added ? 'jdv-seg-added' : s.removed ? 'jdv-seg-removed' : 'jdv-seg-unchanged'}
                >
                  {s.value}
                </span>
              ))}
            </pre>
          : <div className="jdv-inline-diff-loading">Computing diff…</div>
      )}

      {showSideBySide && (
        <div className="jdv-side-by-side">
          <div className="jdv-side jdv-side--before">
            <div className="jdv-side-label">Before</div>
            <pre className="jdv-pre">{before}</pre>
          </div>
          <div className="jdv-side jdv-side--after">
            <div className="jdv-side-label">After</div>
            <pre className="jdv-pre">{after}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

export function JsonDiffViewer({ before, after, ignoreFields, title }: Props) {
  const ignore = ignoreFields || DEFAULT_IGNORE;

  const entries = useMemo(
    () => computeDiff(before, after, ignore),
    [before, after, ignore],
  );

  const changedCount = entries.filter(e => e.kind !== 'unchanged').length;
  const unchangedCount = entries.filter(e => e.kind === 'unchanged').length;

  if (entries.length === 0) {
    return <div className="jdv-empty">No fields to compare</div>;
  }

  return (
    <div className="jdv-root">
      <div className="jdv-summary" style={title ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 } : undefined}>
        <span>
          {changedCount} changed field{changedCount !== 1 ? 's' : ''}
          {unchangedCount > 0 && `, ${unchangedCount} unchanged`}
        </span>
        {title}
      </div>
      <div className="jdv-entries">
        {entries.filter(e => e.kind !== 'unchanged').map(entry => (
          <DiffEntryRow key={entry.key} entry={entry} depth={0} />
        ))}
      </div>
      {unchangedCount > 0 && (
        <UnchangedSection entries={entries.filter(e => e.kind === 'unchanged')} />
      )}
    </div>
  );
}

function UnchangedSection({ entries }: { entries: DiffEntry[] }) {
  const [show, setShow] = useState(false);
  return (
    <div className="jdv-unchanged-section">
      <button className="jdv-unchanged-toggle" onClick={() => setShow(!show)}>
        {show ? '▼' : '▶'} {entries.length} unchanged field{entries.length !== 1 ? 's' : ''}
      </button>
      {show && (
        <div className="jdv-unchanged-list">
          {entries.map(e => (
            <div key={e.key} className="jdv-row jdv-row--unchanged" style={{ paddingLeft: 8 }}>
              <span className="jdv-key">{e.key}</span>
              <span className="jdv-inline-value">
                <span className="jdv-val-unchanged">{formatCompactValue(e.after)}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
